import React, { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import AddFaq from "../../Modals/addFaq";
import { useGetFaqsMutation, useLazyDeleteFaqQuery } from "../../service/cms";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import TableComponent from "../../components/TableComponent";
import { isValidInput } from "../../utils/validations";
import { CommonBody, FaqRes, Permission } from "../../types/General";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { errorToast, successToast } from "../../helpers";
import Loader from "../../constants/Loader";
import { USER_ROLES } from "../../utils/enums";
import useAuth from "../../hooks/useAuth";

type props = {
  hidePermission: Permission | undefined;
};

const ManageFaq = ({ hidePermission }: props) => {
  const userData = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedFaq, setSelectedFaq] = useState<FaqRes | null>(null);
  const [openDelete, setOpenDelete] = useState(false);

  const [anchorEl1, setAnchorEl1] = React.useState<null | HTMLElement>(null);
  const open1 = Boolean(anchorEl1);
  const handleClick1 = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: FaqRes
  ) => {
    setAnchorEl1(event.currentTarget);
    setSelectedId(row?.id);
    setSelectedFaq(row);
  };
  const handleClose1 = () => {
    setAnchorEl1(null);
  };

  const [count, setCount] = useState(0);
  const [faqs, setFaqs] = useState<FaqRes[]>([]);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const [getFaqsList] = useGetFaqsMutation();
  const [deleteFaq] = useLazyDeleteFaqQuery();

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const getFaqs = async () => {
    setLoading(true);
    const body = {
      start: page,
      length: 10,
      search: debouncedSearchTerm.trim(),
    };

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getFaqsList(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setFaqs(response?.data?.response_object || []);
        setCount(response?.data?.total_records || 0);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };
  const handleDelete = async () => {
    try {
      const response = await deleteFaq({ id: selectedId }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        successToast("Deleted successfully");
        getFaqs();
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const columns: GridColDef<(typeof faqs)[number]>[] = [
    {
      flex: 0.8,
      minWidth: 200,
      field: "question",
      headerName: "Question",
      sortable: false,
      filterable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <Tooltip title={row?.question}>
              <p>
                {row?.question?.length > 30
                  ? row?.question?.slice(0, 30) + "..."
                  : row?.question || "-"}
              </p>
            </Tooltip>
          </div>
        );
      },
    },

    {
      flex: 0.8,
      minWidth: 200,
      field: "answer",
      headerName: "Answer",
      sortable: false,
      filterable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <Tooltip title={row?.answer}>
              <p>
                {row?.answer?.length > 30
                  ? row?.answer?.slice(0, 30) + "..."
                  : row?.answer || "-"}
              </p>
            </Tooltip>
          </div>
        );
      },
    },
    ...(userData?.user_role_type === USER_ROLES.ADMIN ||
    hidePermission?.can_add_edit ||
    hidePermission?.can_be_delete
      ? [
          {
            flex: 0.1,
            minWidth: 80,
            field: "action",
            headerName: "Action",
            sortable: false,
            filterable: false,
            renderCell: ({ row }: GridRenderCellParams<any>) => {
              return (
                <div className="table_actions">
                  <Box>
                    <IconButton
                      aria-controls={open1 ? "basic-menu1" : undefined}
                      aria-haspopup="true"
                      aria-expanded={open1 ? "true" : undefined}
                      onClick={(event) => handleClick1(event, row)}
                    >
                      <img src="/static/images/more_icon.svg" alt="" />
                    </IconButton>
                    <Menu
                      className="setting_menu"
                      id="basic-menu1"
                      anchorEl={anchorEl1}
                      open={open1}
                      onClose={handleClose1}
                      MenuListProps={{
                        "aria-labelledby": "basic-button1",
                      }}
                      PaperProps={{
                        elevation: 0,
                        sx: {
                          overflow: "visible",
                          boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.05)",
                          borderRadius: "8px",
                          mt: 1,
                        },
                      }}
                      transformOrigin={{ horizontal: "right", vertical: "top" }}
                      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    >
                      {userData?.user_role_type === USER_ROLES?.ADMIN ||
                      hidePermission?.can_add_edit ? (
                        <MenuItem
                          onClick={() => {
                            setOpenModal(true);
                            handleClose1();
                          }}
                        >
                          Edit
                        </MenuItem>
                      ) : null}
                      {userData?.user_role_type === USER_ROLES?.ADMIN ||
                      hidePermission?.can_be_delete ? (
                        <MenuItem
                          onClick={() => {
                            handleClose1();
                            setOpenDelete(true);
                          }}
                        >
                          Delete
                        </MenuItem>
                      ) : null}
                    </Menu>
                  </Box>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  useEffect(() => {
    getFaqs();
  }, [debouncedSearchTerm, page]);

  return (
    <div>
      <Loader isLoad={loading} />

      <div className="main_title">
        <div className="cards">
          <Box className="custom_tabs" sx={{ width: "100%" }}>
            <div className="table_header">
              <div className="left_s">
                <Box className="search_box">
                  <SearchBar
                    searchTerm={searchTerm}
                    placeholder="Search"
                    setDebouncedSearchTerm={setDebouncedSearchTerm}
                    value={searchTerm}
                    onCross={() => setSearchTerm("")}
                    onChange={(val: any) => {
                      if (isValidInput(val.target.value)) {
                        setSearchTerm(val.target.value);
                      }
                    }}
                  />
                </Box>
              </div>
              <div className="right_s">
                <Box>
                  {userData?.user_role_type === USER_ROLES?.ADMIN ||
                  hidePermission?.can_add_edit ? (
                    <Button
                      className="btn btn_blue"
                      onClick={() => {
                        setSelectedId(null);
                        setSelectedFaq(null);
                        setOpenModal(true);
                      }}
                    >
                      Add faq
                    </Button>
                  ) : null}
                </Box>
              </div>
            </div>

            <TableComponent
              columns={columns}
              rows={faqs}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </Box>
        </div>
      </div>
      <AddFaq
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        getFaqs={getFaqs}
        selectedId={selectedId}
        selectedFaq={selectedFaq}
      />
      <ConfirmationModal
        text={"Are you sure to delete this FAQ?"}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
      />
    </div>
  );
};

export default ManageFaq;
